/**
 * KpaReferral - Gestion du système de parrainage KPA Ton Concours
 * Tables : referral_profiles, referrals
 * Récompenses : crédits IAP, jours premium
 */
import {
  AlertCircle,
  Gift,
  Loader2,
  RefreshCw,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ReferralProfile {
  user_id: string;
  full_name: string;
  email: string;
  referral_code: string;
  total_referrals: number;
  validated_referrals: number;
  total_credits_earned: number;
  created_at: string;
}

interface Referral {
  id: string;
  referrer_name: string;
  referrer_email: string;
  referee_name: string;
  referee_email: string;
  status: 'pending' | 'validated' | 'rewarded' | 'expired';
  reward_type: 'credits' | 'premiumDays';
  reward_value: number;
  created_at: string;
  updated_at: string;
}

interface ReferralStats {
  total_referral_profiles: number;
  total_referrals: number;
  validated_referrals: number;
  pending_referrals: number;
  total_credits_distributed: number;
  conversion_rate: number;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  validated: 'bg-blue-100 text-blue-700',
  rewarded: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-500',
};

const KpaReferral: React.FC = () => {
  const [profiles, setProfiles] = useState<ReferralProfile[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'history'>('leaderboard');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profilesRes, referralsRes, statsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/pka/referrals/profiles?limit=20`),
        authFetch(`${API_BASE_URL}/api/pka/referrals?limit=50`),
        authFetch(`${API_BASE_URL}/api/pka/referrals/stats`),
      ]);
      if (profilesRes.ok) setProfiles((await profilesRes.json()).profiles ?? []);
      if (referralsRes.ok) setReferrals((await referralsRes.json()).referrals ?? []);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900">Parrainage KPA</h1>
          </div>
          <p className="text-gray-500 text-sm">Codes de parrainage → crédits IAP distribués automatiquement</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error} — Les données ne sont pas encore disponibles via l'API.
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {[
            { label: 'Profils parrain', value: stats.total_referral_profiles },
            { label: 'Total parrainages', value: stats.total_referrals },
            { label: 'Validés', value: stats.validated_referrals },
            { label: 'En attente', value: stats.pending_referrals },
            { label: 'Crédits distribués', value: stats.total_credits_distributed },
            { label: 'Taux conversion', value: `${stats.conversion_rate}%` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* How it works */}
      <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
        <h3 className="font-semibold text-orange-800 mb-2 text-sm">🎁 Fonctionnement du parrainage KPA</h3>
        <div className="grid md:grid-cols-3 gap-3 text-xs text-orange-700">
          <div className="bg-orange-100 rounded p-2">
            <strong>1. Code unique</strong> — Chaque utilisateur reçoit un code de parrainage unique généré par le système
          </div>
          <div className="bg-orange-100 rounded p-2">
            <strong>2. Inscription</strong> — Le filleul saisit le code lors de l'onboarding → statut "pending" → "validated"
          </div>
          <div className="bg-orange-100 rounded p-2">
            <strong>3. Récompenses</strong> — Crédits IAP ou jours premium distribués au parrain ET au filleul
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 flex">
          {(['leaderboard', 'history'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition ${activeTab === tab ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'leaderboard' ? '🏆 Top parrains' : '📋 Historique parrainages'}
            </button>
          ))}
        </div>

        {activeTab === 'leaderboard' && (
          <div>
            {profiles.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Users className="w-8 h-8 mx-auto mb-2" />
                <p>Aucun profil parrain pour l'instant</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">#</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Parrain</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Code</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Total filleuls</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Validés</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Crédits gagnés</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {profiles.map((p, idx) => (
                      <tr key={p.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-bold text-gray-400">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{p.full_name}</p>
                          <p className="text-xs text-gray-400">{p.email}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{p.referral_code}</code>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold">{p.total_referrals}</td>
                        <td className="px-4 py-3 text-center text-green-600 font-semibold">{p.validated_referrals}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-orange-600 font-bold">{p.total_credits_earned} cr</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            {referrals.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Gift className="w-8 h-8 mx-auto mb-2" />
                <p>Aucun parrainage enregistré</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Parrain</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Filleul</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Statut</th>
                      <th className="text-center px-4 py-3 text-gray-600 font-medium">Récompense</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {referrals.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-xs">{r.referrer_name}</p>
                          <p className="text-xs text-gray-400">{r.referrer_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-xs">{r.referee_name}</p>
                          <p className="text-xs text-gray-400">{r.referee_email}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[r.status]}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-xs">
                          {r.reward_type === 'credits' ? `${r.reward_value} cr` : `${r.reward_value}j premium`}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {new Date(r.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KpaReferral;
